/* eslint global-require: off */
/* eslint @typescript-eslint/no-var-requires: off */

import { v4 as uuidv4 } from 'uuid';
import * as Auth from '../api/Auth';
import { sopClient } from '../api/Auth';

// disabled in nodejs threads
const hasDOM = typeof window !== 'undefined' && typeof document !== 'undefined';
const debug = process.env.REACT_APP_MODE === 'dev';

export type SopaliticsInitProps = {
  deviceId?: string;
  installationId?: string;
};

type TestTakerAnalyticsEventType = {
  licenseId?: number;
  createdAt: Date;
  payload: {
    name: string;
    installationId?: string;
    deviceId?: string;
    properties: any;
  };
};

const ANONYMOUS_EXTERNAL_ID = 'ANONYMOUS';
let currentExternalId: string | null = null;
let pendingEvents: { [key: string]: TestTakerAnalyticsEventType[] } = {
  [ANONYMOUS_EXTERNAL_ID]: []
};

addEventListener('beforeunload', sendPendingEvents);
setInterval(sendPendingEvents, 2_000);

function sendPendingEvents() {
  if (
    !(Auth.getSopToken && Auth.getSopToken()) ||
    !currentExternalId ||
    !(pendingEvents[currentExternalId].length || pendingEvents[ANONYMOUS_EXTERNAL_ID].length)
  ) {
    return;
  }

  const externalId = currentExternalId;
  const sendingEvents = [...pendingEvents[ANONYMOUS_EXTERNAL_ID], ...pendingEvents[externalId]].map(
    (event) => ({
      ...event,
      ...{
        payload: {
          ...event.payload,
          ...{
            installationId,
            deviceId
          }
        }
      }
    })
  );
  pendingEvents[externalId] = [];
  pendingEvents[ANONYMOUS_EXTERNAL_ID] = [];

  sopClient.sendAnalyticsEvents(sendingEvents).catch((error) => {
    pendingEvents[externalId] = [...sendingEvents, ...pendingEvents[externalId]];

    console.error(error);
  });
}

type GlobalProperties = string | number | null;
let globalProperties: { [index: string]: GlobalProperties } = {};
let userProperties: { [index: string]: GlobalProperties } = {};

let deviceId: string | undefined;
let installationId: string | undefined;
let licenseId: number | undefined;

const init = (sopalitics?: SopaliticsInitProps) => {
  if (debug) {
    console.log('sopaliticsApi.init()');
  }

  if (!hasDOM) {
    console.log('sopaliticsApi is disabled in context');

    return;
  }

  deviceId = sopalitics?.deviceId || fallbackId();
  installationId = sopalitics?.installationId || fallbackId();

  const userAgent = navigator.userAgent;

  register({
    useragent: userAgent,
    locale: navigator?.language,
    resolution: screen?.width + 'x' + screen?.height,
    viewport: window?.innerWidth + 'x' + window?.innerHeight,
    platform: /android/i.test(userAgent)
      ? 'Android'
      : /iPad|iPhone|iPod/.test(userAgent)
      ? 'iOS'
      : 'Desktop',
    orientation: screen?.orientation?.type
  });
};

const setDeviceIds = (sopalitics?: SopaliticsInitProps) => {
  deviceId = sopalitics?.deviceId || deviceId;
  installationId = sopalitics?.installationId || installationId;
};

const setLicenseId = (_licenseId?: number | string) => {
  if (typeof _licenseId === 'string') {
    licenseId = parseInt(_licenseId);
  } else {
    licenseId = _licenseId;
  }
};

const fallbackId = () => {
  let sid = localStorage.getItem('ref-sid');
  if (!sid) {
    sid = uuidv4();
  }

  localStorage.setItem('ref-sid', sid);

  return sid;
};

const updateTracking = (_track: boolean) => {
  // NOP
};

const onLogin = (externalId: string) => {
  if (debug) {
    console.log('sopaliticsApi.onLogin()', externalId);
  }

  if (!hasDOM) {
    return;
  }

  pendingEvents[externalId] = [...(pendingEvents[externalId] || [])];
  currentExternalId = externalId;

  userProperties = {};
};

const onLogout = () => {
  if (debug) {
    console.log('sopaliticsApi.onLogout()');
  }

  if (!hasDOM) {
    return;
  }

  currentExternalId = null;
  userProperties = {};
};

const event = (name: string, properties: Record<string, unknown> = {}) => {
  if (debug) {
    console.log(
      'sopaliticsApi.event()',
      currentExternalId || ANONYMOUS_EXTERNAL_ID,
      name,
      properties
    );
  }

  if (!hasDOM) {
    return;
  }

  pendingEvents[currentExternalId || ANONYMOUS_EXTERNAL_ID].push({
    licenseId,
    createdAt: new Date(),
    payload: {
      name,
      installationId,
      deviceId,
      properties: { ...globalProperties, ...userProperties, ...properties }
    }
  });
};

const register = (properties: { [index: string]: GlobalProperties }) => {
  if (debug) {
    console.log('sopaliticsApi.register()', properties);
  }

  if (!hasDOM) {
    return;
  }

  globalProperties = { ...globalProperties, ...properties };
};

const setProperties = (properties: { [index: string]: GlobalProperties }) => {
  if (debug) {
    console.log('sopaliticsApi.setProperties()', properties);
  }

  if (!hasDOM) {
    return;
  }

  userProperties = { ...userProperties, ...properties };
};

export const sopaliticsApi = {
  init,
  updateTracking,
  onLogin,
  onLogout,
  register,
  event,
  setProperties,
  setDeviceIds,
  setLicenseId
};
